import type { AppAbility } from "@/features/auth/casl/types"
import type { WorkspaceAccessRecord } from "@/features/auth/lib/workspace-user-access"
import {
  TenantAuthorizationAction,
  TenantAuthorizationContext,
} from "@ibada/shared-types/authorization"

export {
  TenantAuthorizationAction,
  TenantAuthorizationContext,
} from "@ibada/shared-types/authorization"

export enum TenantAuthorizationDecisionStatus {
  Resolving = "RESOLVING",
  Allowed = "ALLOWED",
  Denied = "DENIED",
  EntitlementMissing = "ENTITLEMENT_MISSING",
}

/** Rendering lifecycle used by shared route guards: protected content is valid only in Allow. */
export enum TenantAuthorizationRouteGuardState {
  Loading = "LOADING",
  Allow = "ALLOW",
  Deny = "DENY",
}

export enum TenantAuthorizationDenialReason {
  AuthorizationStillResolving = "AUTHORIZATION_STILL_RESOLVING",
  NoActiveWorkspace = "NO_ACTIVE_WORKSPACE",
  MissingBranchContext = "MISSING_BRANCH_CONTEXT",
  MissingBranchAssignment = "MISSING_BRANCH_ASSIGNMENT",
  MissingPermission = "MISSING_PERMISSION",
  UnknownAction = "UNKNOWN_ACTION",
  EntitlementMissing = "ENTITLEMENT_MISSING",
}

/** Reserved for future plan/feature gating. It intentionally does not enforce billing today. */
export enum TenantFeatureEntitlementStatus {
  NotConfigured = "NOT_CONFIGURED",
  Missing = "MISSING",
}

export type ResolvedTenantAuthorizationContext = {
  kind: TenantAuthorizationContext
  branchId: string | null
}

export type TenantAuthorizationDecision = {
  status: TenantAuthorizationDecisionStatus
  context: ResolvedTenantAuthorizationContext
  action: TenantAuthorizationAction
  denialReason?: TenantAuthorizationDenialReason
  featureEntitlement: { status: TenantFeatureEntitlementStatus; key?: string }
}

/** Input snapshot for the pure resolver. The hook owns loading the snapshot. */
export type TenantAuthorizationInput = {
  access: WorkspaceAccessRecord | null
  ability: AppAbility
  requestedBranchSlug?: string | null
  isResolving?: boolean
  /** Future subscription/plan lookup. Undefined means entitlement enforcement is not configured. */
  isFeatureEntitled?: (featureKey: string) => boolean | undefined
}
